import type { Country } from "react-phone-number-input";

import { CreditCardIcon, LandmarkIcon, ShieldCheck } from "lucide-react";
import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Controller } from "react-hook-form";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

import type {
  AttachCustomerPayload,
  SubscriptionFormHandle,
  SubscriptionFormProps,
} from "@/features/auth/subscription/components/SubscriptionType.ts";

import { Button } from "@/components/button";
import { Checkbox, CheckboxField } from "@/components/checkbox";
import {
  ClearableInput,
  ClearButton,
  FieldError,
  Label,
  TextField,
} from "@/components/field";
import { SegmentedTabs } from "@/components/segmented-tabs";
import { env } from "@/config/env";
import { pushDataLayer } from "@/lib/dataLayer";

import type {
  CardChangeEventType,
  ElementsType,
  ElementType,
  StripeInterface,
  StripeType,
} from "./StripeInterface.ts";

import { checkBankBalance, finalizeAchCheckout } from "../api/achCheckout.api";
import { convertCheckoutLead } from "../api/checkoutLeads.api";
import { sendPurchaseConfirmed } from "../api/purchaseConfirmed.api";
import {
  type SubscriptionFormData,
  useSubscriptionForm,
} from "../hooks/useSubscriptionForm";
import {
  INSUFFICIENT_FUNDS_MESSAGE,
  waitForAchFailure,
} from "../utils/achPayment";
import {
  checkoutPrimaryButtonClassName,
  stripeFieldClassName,
} from "./checkoutStyles";

type CheckoutPaymentMethod = Extract<
  SubscriptionFormData["paymentMethod"],
  "bank" | "card"
>;

function getBrowserCountry(): Country | undefined {
  const lang = navigator.language || "en-US";
  const parts = lang.split("-");

  return parts[1]?.toUpperCase() as Country;
}

const CARD_FIELDS = [
  {
    containerId: "card-number-element",
    errorCodeFragment: "number",
    placeholder: "1234 1234 1234 1234",
    type: "cardNumber",
  },
  {
    containerId: "card-expiry-element",
    errorCodeFragment: "expiry",
    placeholder: "MM / YY",
    type: "cardExpiry",
  },
  {
    containerId: "card-cvc-element",
    errorCodeFragment: "cvc",
    placeholder: "CVC",
    type: "cardCvc",
  },
] as const;

type CardFieldType = (typeof CARD_FIELDS)[number]["type"];

// Stripe error codes name the field they belong to, e.g. incomplete_number or invalid_expiry_year.
function cardFieldForErrorCode(code?: string): CardFieldType | undefined {
  return CARD_FIELDS.find((field) => code?.includes(field.errorCodeFragment))
    ?.type;
}

function readColorToken(name: string): string {
  const channels = getComputedStyle(document.body)
    .getPropertyValue(name)
    .trim();

  return channels ? `rgb(${channels.split(/\s+/).join(", ")})` : "";
}

// Stripe Elements render in iframes, so our CSS tokens are read and passed in as plain values.
function stripeFieldStyle() {
  return {
    base: {
      "::placeholder": { color: readColorToken("--text-placeholder") },
      color: readColorToken("--text-primary"),
      fontFamily: getComputedStyle(document.body).fontFamily,
      fontSize: "16px",
    },
    invalid: { color: readColorToken("--text-error-primary") },
  };
}

const ACH_ELEMENT_CONTAINER_ID = "ach-payment-element";

function achElementAppearance() {
  return {
    variables: {
      colorDanger: readColorToken("--text-error-primary"),
      colorText: readColorToken("--text-primary"),
      colorTextPlaceholder: readColorToken("--text-placeholder"),
      fontFamily: getComputedStyle(document.body).fontFamily,
    },
  };
}

function extractStripeIds(customerData: unknown): {
  invoiceId: string | undefined;
  subscriptionId: string | undefined;
} {
  if (!customerData || typeof customerData !== "object") {
    return { invoiceId: undefined, subscriptionId: undefined };
  }

  const { invoice, invoice_id, subscription, subscription_id } =
    customerData as {
      invoice?: { id?: string };
      invoice_id?: string;
      subscription?: {
        id?: string;
        latest_invoice?: { id?: string } | string;
        latest_invoice_id?: string;
      };
      subscription_id?: string;
    };
  const latestInvoice =
    typeof subscription?.latest_invoice === "string"
      ? subscription.latest_invoice
      : subscription?.latest_invoice?.id;
  const invoiceId =
    subscription?.latest_invoice_id ??
    latestInvoice ??
    invoice?.id ??
    invoice_id;
  const subscriptionId = subscription?.id ?? subscription_id;

  if (typeof invoiceId !== "string" || typeof subscriptionId !== "string") {
    console.warn(
      "Could not find Stripe invoice/subscription ids in the attach-customer response. Top-level keys:",
      Object.keys(customerData as Record<string, unknown>),
    );
  }

  return {
    invoiceId: typeof invoiceId === "string" ? invoiceId : undefined,
    subscriptionId:
      typeof subscriptionId === "string" ? subscriptionId : undefined,
  };
}

const SubscriptionForm = forwardRef<
  SubscriptionFormHandle,
  SubscriptionFormProps
>(
  (
    {
      chargeSummary = "",
      checkoutLeadId,
      initialEmail,
      isAchEnabled = false,
      isSubmitDisabled = false,
      isSubmitting = false,
      onChangeEmail,
      onSubmit,
      onSubmitPress,
      onSubmittingChange,
      promotionCode,
      promotionCodeId,
      promotionRef,
      submitLabel = "Start subscription",
      subscriptionApiData,
    },
    ref,
  ) => {
    const { control, formState, handleSubmit, setValue, watch } =
      useSubscriptionForm(isAchEnabled ? "bank" : "card");
    const { dirtyFields, errors } = formState;

    const paymentMethod = watch("paymentMethod");
    const fullName = watch("fullName");
    const companyName = watch("companyName");
    const email = watch("email");
    const phoneNumber = watch("phoneNumber");
    const contactSectionEventSentRef = useRef(false);
    const stripeRef = useRef<null | StripeType>(null);
    const elementsRef = useRef<ElementsType | null>(null);
    const achElementsRef = useRef<ElementsType | null>(null);
    const cardFieldsRef = useRef<Partial<Record<CardFieldType, ElementType>>>(
      {},
    );
    const completeCardFieldsRef = useRef<
      Partial<Record<CardFieldType, boolean>>
    >({});
    const [invalidCardFields, setInvalidCardFields] = useState<
      Partial<Record<CardFieldType, boolean>>
    >({});
    const [stripeError, setStripeError] = useState<null | string>(null);

    const pendingBankCheckoutRef = useRef<{
      clientSecret: string;
      invoiceId?: string;
      subscriptionId?: string;
    } | null>(null);

    const publishableKeyProp =
      subscriptionApiData?.publishable_key || undefined;
    const publishableKey = (publishableKeyProp ||
      (import.meta as ImportMeta).env?.VITE_STRIPE_PUBLISHABLE_KEY) as
      | string
      | undefined;

    useEffect(() => {
      if (!publishableKey) {
        console.warn("Missing VITE_STRIPE_PUBLISHABLE_KEY env var");

        return;
      }

      // verify if stripe scripts were initialized
      const StripeCtor = (window as StripeInterface).Stripe;

      if (!StripeCtor) return;

      if (!stripeRef.current) {
        stripeRef.current = StripeCtor(publishableKey);
      }
      if (!elementsRef.current && stripeRef.current) {
        elementsRef.current = stripeRef.current.elements();
      }

      if (paymentMethod === "card" && elementsRef.current) {
        const style = stripeFieldStyle();

        for (const field of CARD_FIELDS) {
          let element =
            cardFieldsRef.current[field.type] ??
            elementsRef.current.getElement?.(field.type) ??
            null;

          if (!element) {
            element = elementsRef.current.create(field.type, {
              placeholder: field.placeholder,
              style,
            });
          }
          if (!cardFieldsRef.current[field.type]) {
            element.on?.("change", (event: CardChangeEventType) => {
              completeCardFieldsRef.current[field.type] = !!event.complete;
              setInvalidCardFields((current) => ({
                ...current,
                [field.type]: !!event.error,
              }));
              setStripeError(null);
            });
            cardFieldsRef.current[field.type] = element;
          }

          const container = document.querySelector(`#${field.containerId}`);

          if (container && container.childElementCount === 0) {
            element.mount(`#${field.containerId}`);
          }
        }
      }

      const price = subscriptionApiData?.price;

      if (
        paymentMethod === "bank" &&
        stripeRef.current &&
        !achElementsRef.current &&
        typeof price?.unit_amount === "number"
      ) {
        // Deferred intent: the subscription and its PaymentIntent only exist after attach-customer on submit.
        achElementsRef.current = stripeRef.current.elements({
          amount: price.unit_amount * (subscriptionApiData?.quantity ?? 1),
          appearance: achElementAppearance(),
          currency: price.currency,
          mode: "subscription",
          paymentMethodCreation: "manual",
          paymentMethodOptions: {
            us_bank_account: {
              financial_connections: {
                permissions: ["payment_method", "balances"],
              },
              verification_method: "instant",
            },
          },
          paymentMethodTypes: ["us_bank_account"],
        });
        const achPaymentElement = achElementsRef.current.create("payment", {
          fields: { billingDetails: { email: "never", name: "never" } },
        });

        achPaymentElement.on?.("change", () => setStripeError(null));
        achPaymentElement.mount(`#${ACH_ELEMENT_CONTAINER_ID}`);
      }
    }, [
      paymentMethod,
      publishableKey,
      onSubmit,
      onSubmittingChange,
      subscriptionApiData,
      watch,
    ]);

    // Populate a form with API-derived data when available
    useEffect(() => {
      if (!subscriptionApiData) return;

      const { intent, prefill, price, product, publishable_key, quantity } =
        subscriptionApiData;

      if (intent) {
        setValue("paymentIntentId", intent.id);
        setValue("paymentIntentClientSecret", intent.client_secret);
        setValue("amount", intent.amount);
        setValue("currency", intent.currency);
      }

      if (product) {
        setValue("productId", product.id);
      }
      if (price) {
        setValue("priceId", price.id);
      }
      if (typeof quantity === "number") setValue("quantity", quantity);
      if (publishable_key) setValue("publishableKey", publishable_key);

      if (prefill) {
        if (prefill.name) setValue("fullName", prefill.name);
        if (prefill.company_name) setValue("companyName", prefill.company_name);
        if (prefill.email) setValue("email", prefill.email);
        if (prefill.phone) {
          const normalizedPhone = prefill.phone.startsWith("+")
            ? prefill.phone
            : `+${prefill.phone}`;

          setValue("phoneNumber", normalizedPhone);
        }
      }
    }, [subscriptionApiData, setValue]);

    useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      const receiptEmail = params.get("receipt_email");

      if (!receiptEmail) return;

      const backendEmail = subscriptionApiData?.prefill?.email ?? null;

      if (!backendEmail) {
        setValue("email", receiptEmail);
      }
    }, [setValue, subscriptionApiData]);

    useEffect(() => {
      if (initialEmail) setValue("email", initialEmail);
    }, [initialEmail, setValue, subscriptionApiData]);

    useEffect(() => {
      if (contactSectionEventSentRef.current) return;

      const completed = [fullName, companyName, email, phoneNumber].every(
        (value) => !!value?.trim(),
      );
      const edited = !!(
        dirtyFields.fullName ||
        dirtyFields.companyName ||
        dirtyFields.email ||
        dirtyFields.phoneNumber
      );
      const valid = !(
        errors.fullName ||
        errors.companyName ||
        errors.email ||
        errors.phoneNumber
      );

      if (!completed || !edited || !valid) return;

      contactSectionEventSentRef.current = true;
      pushDataLayer({
        event: "checkout_form_interaction",
        page_url: "subscription",
        section: "checkout_form",
      });
    }, [fullName, companyName, email, phoneNumber, dirtyFields, errors]);

    const createCardPaymentMethod = async (
      stripe: StripeType,
      data: SubscriptionFormData,
    ) => {
      if (!cardFieldsRef.current.cardNumber) {
        setStripeError("Payment form is not ready yet. Please wait a moment.");

        return null;
      }

      // Stripe only reports the first incomplete field, so flag all of them at once.
      const incompleteCardFields = CARD_FIELDS.filter(
        ({ type }) => !completeCardFieldsRef.current[type],
      );

      if (incompleteCardFields.length > 0) {
        setInvalidCardFields((current) => ({
          ...current,
          ...Object.fromEntries(
            incompleteCardFields.map(({ type }) => [type, true]),
          ),
        }));

        return null;
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        billing_details: {
          email: data.email,
          name: data.nameOnCard || data.fullName,
        },
        card: cardFieldsRef.current.cardNumber,
        type: "card",
      });

      if (error || !paymentMethod) {
        const invalidField = cardFieldForErrorCode(error?.code);

        if (invalidField) {
          setStripeError(null);
          setInvalidCardFields((current) => ({
            ...current,
            [invalidField]: true,
          }));
        } else {
          setStripeError(error?.message || "Unable to create payment method");
        }

        return null;
      }

      return paymentMethod;
    };

    const onSubmitInternal = async (data: SubscriptionFormData) => {
      onSubmittingChange?.(true);

      if (data.paymentMethod === "card" || data.paymentMethod === "bank") {
        let invoiceId: string | undefined;
        let subscriptionId: string | undefined;

        if (!publishableKey) {
          setStripeError(
            "Payment configuration error. Please try again later.",
          );
          onSubmittingChange?.(false);

          return;
        }
        if (!stripeRef.current) {
          setStripeError(
            "Payment form is not ready yet. Please wait a moment.",
          );
          onSubmittingChange?.(false);

          return;
        }

        let paymentMethodId: string | undefined;
        const achElements = achElementsRef.current;

        if (data.paymentMethod === "bank") {
          // Validates that a bank account was connected before any subscription is created.
          const submitResult = await achElements?.submit?.();

          if (!achElements || submitResult?.error) {
            setStripeError(
              submitResult?.error?.message ||
                "Payment form is not ready yet. Please wait a moment.",
            );
            onSubmittingChange?.(false);

            return;
          }

          const { error, paymentMethod: bankPaymentMethod } =
            await stripeRef.current.createPaymentMethod({
              elements: achElements,
              params: {
                billing_details: { email: data.email, name: data.fullName },
              },
            });

          if (error || !bankPaymentMethod) {
            setStripeError(error?.message || "Unable to connect bank account");
            onSubmittingChange?.(false);

            return;
          }

          const dueTodayCents =
            promotionRef?.current?.finalAmountCents ??
            (subscriptionApiData?.price?.unit_amount ?? 0) *
              (subscriptionApiData?.quantity ?? 1);
          const balance = await checkBankBalance(
            bankPaymentMethod.id,
            dueTodayCents,
          ).catch(() => ({ sufficient: true }));

          if (!balance.sufficient) {
            setStripeError(INSUFFICIENT_FUNDS_MESSAGE);
            onSubmittingChange?.(false);

            return;
          }

          paymentMethodId = bankPaymentMethod.id;
        }

        if (data.paymentMethod === "card") {
          const pm = await createCardPaymentMethod(stripeRef.current, data);

          if (!pm) {
            onSubmittingChange?.(false);

            return;
          }

          paymentMethodId = pm.id;
          setValue("stripePaymentMethodId", pm.id, { shouldValidate: true });
        }

        let clientSecret =
          subscriptionApiData?.intent?.client_secret ||
          data.paymentIntentClientSecret;

        if (!clientSecret) {
          setStripeError("Missing payment intent. Please refresh the page.");
          onSubmittingChange?.(false);

          return;
        }

        const pendingBankCheckout =
          data.paymentMethod === "bank" ? pendingBankCheckoutRef.current : null;

        if (pendingBankCheckout) {
          ({ clientSecret, invoiceId, subscriptionId } = pendingBankCheckout);
        } else {
          // create/attach customer to correct intent
          try {
            const base = env.VITE_PHP_URL.replace(/\/$/, "");

            // Resolve promo values at submitting time, preferring ref over props
            const resolvedPromotionCode =
              promotionRef?.current?.code || promotionCode || null;
            const resolvedPromotionCodeId =
              promotionRef?.current?.id || promotionCodeId || null;

            const payload: AttachCustomerPayload = {
              company_name: data.companyName,
              email: data.email,
              name: data.fullName,
              payment_method_id:
                data.paymentMethod === "card" ? paymentMethodId : undefined,
              phone: data.phoneNumber,
              promotion_code: resolvedPromotionCode,
              promotion_code_id: resolvedPromotionCodeId,
              setup_intent_id: subscriptionApiData?.intent?.id,
            };

            //TODO transfer this service to Node
            const response = await fetch(`${base}/api/stripe/attach-customer`, {
              body: JSON.stringify(payload),
              headers: { "Content-Type": "application/json" },
              method: "POST",
            });

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));

              throw new Error(
                errorData.message || "Failed to create/attach customer",
              );
            }

            const customerData = await response.json();

            ({ invoiceId, subscriptionId } = extractStripeIds(customerData));
            // Some backends return an updated client_secret after attaching the customer
            const updatedSecret =
              customerData?.intent?.client_secret ||
              customerData?.client_secret;

            if (
              typeof updatedSecret === "string" &&
              updatedSecret.length > 0 &&
              updatedSecret !== clientSecret
            ) {
              console.log(
                "Using updated client_secret from backend after attach.",
              );
              clientSecret = updatedSecret;
            }
          } catch (error_: unknown) {
            console.error("Customer creation/attachment error:", error_);
            const message =
              typeof error_ === "object" && error_ && "message" in error_
                ? String((error_ as { message?: unknown }).message ?? "")
                : "";

            setStripeError(message || "Failed to process customer information");
            onSubmittingChange?.(false);

            return;
          }
        }

        if (data.paymentMethod === "bank") {
          pendingBankCheckoutRef.current = {
            clientSecret,
            invoiceId,
            subscriptionId,
          };
        }

        const result =
          data.paymentMethod === "bank"
            ? await stripeRef.current.confirmPayment({
                clientSecret,
                confirmParams: {
                  payment_method: paymentMethodId,
                  return_url: window.location.href,
                },
                redirect: "if_required",
              })
            : await stripeRef.current.confirmCardPayment(clientSecret, {
                payment_method: paymentMethodId,
              });

        if (result.error) {
          setStripeError(result.error.message || null);
          onSubmittingChange?.(false);

          return;
        }

        if (data.paymentMethod === "bank" && result.paymentIntent) {
          const achFailure = await waitForAchFailure(
            stripeRef.current,
            clientSecret,
          );

          if (achFailure) {
            setStripeError(achFailure);
            onSubmittingChange?.(false);

            return;
          }

          await finalizeAchCheckout(result.paymentIntent.id).catch(
            (finalizeError) => {
              console.error("Failed to finalize ACH checkout:", finalizeError);
            },
          );
        }

        const confirmedPaymentMethod = result.paymentIntent?.payment_method;

        paymentMethodId ??=
          typeof confirmedPaymentMethod === "string"
            ? confirmedPaymentMethod
            : confirmedPaymentMethod?.id;

        //TODO remove consoles after testing.
        try {
          console.log("[MetaCAPI] amount sources", {
            "data.amount": data.amount,
            "promotionRef.current?.finalAmountCents":
              promotionRef?.current?.finalAmountCents,
            "subscriptionApiData?.intent": subscriptionApiData?.intent,
            "subscriptionApiData?.price": subscriptionApiData?.price,
          });

          const amountCents =
            promotionRef?.current?.finalAmountCents ??
            subscriptionApiData?.price?.unit_amount ??
            subscriptionApiData?.intent?.amount ??
            data.amount ??
            0;

          const purchaseConfirmedPayload = {
            amount: amountCents / 100,
            currency:
              subscriptionApiData?.price?.currency ??
              subscriptionApiData?.intent?.currency ??
              (data.currency || undefined),
            email: data.email,
            invoiceId: invoiceId || subscriptionId || "pending",
            productId: data.productId || "",
          };

          console.log(
            "[MetaCAPI] sending purchase-confirmed",
            purchaseConfirmedPayload,
          );

          const response = await sendPurchaseConfirmed(
            purchaseConfirmedPayload,
          );

          console.log(
            "[MetaCAPI] purchase-confirmed response",
            response.status,
          );
        } catch (error_) {
          console.error(
            "[MetaCAPI] Failed to send purchase confirmation:",
            error_,
          );
        }

        if (checkoutLeadId) {
          // Not awaited: a slow lead update must never hold back a customer who already paid.
          convertCheckoutLead(checkoutLeadId).catch((convertError) => {
            console.error("Failed to convert checkout lead:", convertError);
          });
        }

        await onSubmit({
          ...data,
          invoiceId,
          stripePaymentMethodId: paymentMethodId,
          subscriptionId,
        });

        return;
      }
      await onSubmit(data);
    };

    useImperativeHandle(
      ref,
      () => ({
        submit: async () => {
          console.log("Form values:", watch());
          await handleSubmit(onSubmitInternal, (errors) => {
            console.log("Validation errors:", errors);
          })();
        },
      }),
      [handleSubmit, onSubmitInternal],
    );

    const browserCountry = getBrowserCountry();
    const termsLabelId = useId();

    return (
      <form
        className="flex flex-col gap-6"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmitPress?.();
        }}
      >
        <Controller
          control={control}
          name="email"
          render={({ field: { name, value }, fieldState: { error } }) => (
            <div>
              <div className="flex items-stretch overflow-hidden rounded-md border border-border-primary bg-bg-primary shadow-xs">
                <input
                  aria-label="Email"
                  className="min-w-0 flex-1 bg-transparent px-[14px] py-[10px] text-md text-text-placeholder outline-hidden"
                  name={name}
                  readOnly
                  value={value}
                />
                {onChangeEmail && (
                  <Button
                    className="rounded-[0] border-0"
                    color="gray"
                    isDisabled={isSubmitting}
                    onPress={onChangeEmail}
                    size="md"
                    variant="secondary"
                  >
                    Change
                  </Button>
                )}
              </div>
              {error && (
                <p className="mt-sm text-sm text-text-error-primary">
                  {error.message}
                </p>
              )}
            </div>
          )}
        />

        <div className="flex flex-col gap-2">
          <h1 className="text-display-sm font-bold text-text-primary">
            Complete your subscription
          </h1>
          <p className="text-sm text-text-secondary">
            You&apos;re one step away from scalable design support with NoLimit
            Creatives
          </p>
        </div>

        <section className="flex flex-col gap-6">
          <h2 className="text-lg font-bold text-text-primary">
            Contact information
          </h2>
          <Controller
            control={control}
            name="fullName"
            render={({
              field: { name, onBlur, onChange, ref, value },
              fieldState: { error, invalid },
            }) => (
              <TextField
                isInvalid={invalid}
                isRequired
                name={name}
                onBlur={onBlur}
                validationBehavior="aria"
              >
                <Label>Full name</Label>
                <ClearableInput
                  onChange={onChange}
                  onClear={() => onChange("")}
                  placeholder="Enter your full name"
                  ref={ref}
                  showClearButton={!!value}
                  value={value}
                />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <div className="flex flex-col gap-6">
            <Controller
              control={control}
              name="companyName"
              render={({
                field: { name, onBlur, onChange, ref, value },
                fieldState: { error, invalid },
              }) => (
                <TextField
                  isInvalid={invalid}
                  isRequired
                  name={name}
                  onBlur={onBlur}
                  validationBehavior="aria"
                >
                  <Label>Company name</Label>
                  <ClearableInput
                    onChange={onChange}
                    onClear={() => onChange("")}
                    placeholder="Enter your company name"
                    ref={ref}
                    showClearButton={!!value}
                    value={value}
                  />
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />
            <Controller
              control={control}
              name="phoneNumber"
              render={({
                field: { name, onChange, value },
                fieldState: { error, invalid },
              }) => (
                <TextField
                  isInvalid={invalid}
                  isRequired
                  name={name}
                  validationBehavior="aria"
                  value={value}
                >
                  <Label>Phone number</Label>
                  <div
                    className="isolate grid grid-cols-[1fr_calc(theme(size.6)+16px)]"
                    data-ui="control"
                  >
                    <PhoneInput
                      aria-invalid={invalid || undefined}
                      className={[
                        "col-span-full row-start-1",
                        value ? "pr-10" : "",
                        // Match Input styles on the container
                        "w-full text-text-primary rounded-md border outline-hidden border-border-primary bg-bg-primary",
                        "px-[14px] py-[10px]",
                        "text-md",
                        // Focus ring on container when inner input is focused
                        "focus-within:ring-4 focus-within:ring-border-brand/25 focus-within:border-border-brand",
                        // Error state
                        invalid
                          ? "border-border-error focus-within:ring-border-error/25 focus-within:border-border-error"
                          : "",
                        // Remove default lib spacing and align children
                        "flex items-center gap-2",
                        "[&_.PhoneInputInput]:w-full [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:outline-none",
                        "[&_.PhoneInputInput]:border-0 [&_.PhoneInputInput]:p-0 [&_.PhoneInputInput]:m-0",
                        "[&_.PhoneInputInput]:placeholder:text-text-placeholder",
                        // Country select/button styling to blend in
                        "[&_.PhoneInputCountry]:m-0 [&_.PhoneInputCountry]:me-2 [&_.PhoneInputCountry]:relative",
                        // Make native select cover the flag button but invisible so it's clickable
                        "[&_.PhoneInputCountrySelect]:absolute [&_.PhoneInputCountrySelect]:inset-0 [&_.PhoneInputCountrySelect]:opacity-0 [&_.PhoneInputCountrySelect]:cursor-pointer [&_.PhoneInputCountrySelect]:block",
                      ].join(" ")}
                      countryCallingCodeEditable={false}
                      defaultCountry={browserCountry}
                      international
                      onChange={(val) => onChange(val || "")}
                      placeholder="Enter your phone number"
                      value={value || ""}
                    />
                    {value && (
                      <ClearButton
                        className="z-10 -col-end-1 row-start-1 place-self-center"
                        onPress={() => onChange("")}
                      />
                    )}
                  </div>
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />
          </div>
        </section>

        <section className="mt-8 flex flex-col gap-6">
          <h2 className="text-lg font-semibold text-text-primary">
            Payment information
          </h2>
          {isAchEnabled && (
            <SegmentedTabs<CheckoutPaymentMethod>
              className="w-full divide-x-0 p-px"
              onTabChange={(method) => {
                setValue("paymentMethod", method);
                setStripeError(null);
              }}
              selectedTab={paymentMethod === "bank" ? "bank" : "card"}
              tabClassName="flex-1 justify-center rounded-md py-[10px] text-md"
              tabs={[
                {
                  icon: <CreditCardIcon aria-hidden="true" size={20} />,
                  id: "card",
                  label: "Card",
                },
                {
                  icon: <LandmarkIcon aria-hidden="true" size={20} />,
                  id: "bank",
                  label: (
                    <>
                      <span className="sm:hidden">Bank (ACH)</span>
                      <span className="hidden sm:inline">
                        Bank account (ACH)
                      </span>
                    </>
                  ),
                },
              ]}
            />
          )}
          <div
            hidden={paymentMethod !== "bank"}
            id={ACH_ELEMENT_CONTAINER_ID}
          />
          {/* Hidden rather than unmounted so the Stripe card iframes stay mounted. */}
          <div
            className="flex flex-col gap-6"
            hidden={paymentMethod !== "card"}
          >
            <div className="flex flex-col gap-1.5">
              <Label>Card number</Label>
              <div
                className={stripeFieldClassName}
                data-invalid={invalidCardFields.cardNumber || undefined}
                id="card-number-element"
              />
              {invalidCardFields.cardNumber && (
                <p className="text-sm text-text-error-primary">
                  Please enter a valid card number
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="flex flex-col gap-1.5">
                <Label>Expiration</Label>
                <div
                  className={stripeFieldClassName}
                  data-invalid={invalidCardFields.cardExpiry || undefined}
                  id="card-expiry-element"
                />
                {invalidCardFields.cardExpiry && (
                  <p className="text-sm text-text-error-primary">
                    Invalid expiration date
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Security code</Label>
                <div
                  className={stripeFieldClassName}
                  data-invalid={invalidCardFields.cardCvc || undefined}
                  id="card-cvc-element"
                />
                {invalidCardFields.cardCvc && (
                  <p className="text-sm text-text-error-primary">
                    Invalid security code
                  </p>
                )}
              </div>
            </div>
            <Controller
              control={control}
              name="nameOnCard"
              render={({
                field: { name, onBlur, onChange, ref, value },
                fieldState: { error, invalid },
              }) => (
                <TextField
                  isInvalid={invalid}
                  isRequired
                  name={name}
                  onBlur={onBlur}
                  validationBehavior="aria"
                >
                  <Label>Name on card</Label>
                  <ClearableInput
                    onChange={onChange}
                    onClear={() => onChange("")}
                    placeholder="Name as it appears on your card"
                    ref={ref}
                    showClearButton={!!value}
                    value={value}
                  />
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />
          </div>
          {stripeError && (
            <p className="-mt-3 text-sm text-text-error-primary">
              {stripeError}
            </p>
          )}
        </section>

        <div className="flex items-center gap-2 rounded-md bg-bg-success-primary px-4 py-3 text-sm text-text-success-primary">
          <ShieldCheck aria-hidden className="size-5 shrink-0" />
          Cancel anytime. No contracts, no cancellation fees.
        </div>

        <Controller
          control={control}
          name="termsAccepted"
          render={({
            field: { name, onChange, value },
            fieldState: { error, invalid },
          }) => (
            <CheckboxField className="gap-1.5">
              <div className="flex items-start gap-2">
                <Checkbox
                  aria-labelledby={termsLabelId}
                  className="mt-0.5 data-invalid:[&>div]:border-border-error"
                  indicatorClassName="border-utility-gray-800 bg-utility-gray-800"
                  isInvalid={invalid}
                  isSelected={value}
                  name={name}
                  onChange={onChange}
                />
                <span className="text-sm text-text-secondary" id={termsLabelId}>
                  I agree to NoLimit Creatives&apos;{" "}
                  <a
                    className="font-medium text-text-primary underline"
                    href="https://nlc.com/terms-of-service"
                    rel="noreferrer"
                    target="_blank"
                  >
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a
                    className="font-medium text-text-primary underline"
                    href="https://nlc.com/privacy-policy"
                    rel="noreferrer"
                    target="_blank"
                  >
                    Privacy Policy
                  </a>
                  .
                </span>
              </div>
              {error?.message && (
                <p className="text-sm text-text-error-primary" role="alert">
                  {error.message}
                </p>
              )}
            </CheckboxField>
          )}
        />

        <p className="text-sm text-text-tertiary">
          By starting your subscription, you authorize NoLimit Creatives to
          {paymentMethod === "bank"
            ? "debit your bank account"
            : "charge your card"}{" "}
          for future payments under these terms. You can cancel at any time.
        </p>

        <div className="flex flex-col gap-4">
          <Button
            className={checkoutPrimaryButtonClassName(isSubmitting)}
            isDisabled={isSubmitting || isSubmitDisabled}
            isLoading={isSubmitting}
            loadingLabel="Processing payment"
            onPress={onSubmitPress}
            size="lg"
          >
            {submitLabel}
          </Button>
          <p className="text-center text-xs text-text-tertiary">
            {chargeSummary}
          </p>
        </div>
      </form>
    );
  },
);

export default SubscriptionForm;

SubscriptionForm.displayName = "SubscriptionForm";
